import { Box, Grid, Typography } from "@mui/material";
import ReBox from "../../../../common/ReBox";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SISideCard from "../SISideCard/SISideCard";
import { ReactComponent as Ticket } from "../Svgs/SRC-Portal-Icons_Entry.svg";
import { useContext } from "react";
import { GlobalContext } from "../../../../common/Context";
import moment from "moment";
import { useTranslation } from "react-i18next";
import PreviousWinners from "../PreviousWinners/PreviousWinners";
import { CalendarIcon, GiftIcon, TotalIcon } from "../../../../common/Icons";
import { blue, green, purple, white } from "../../../../../theme/colours";
import MainAreaSection from "../MainAreaSection";
import { CHALLENGES_SELF_IMPROVEMENT_AREA } from "../../../../../constants/ids";

const SelfImprovement = ({ data, winners }) => {
  const { mobileView, desktopView } = useContext(GlobalContext);
  const { t } = useTranslation("CompetitionInfo");
  const endDate = moment().day(7).format("MMMM D, YYYY");

  const containerStyle = {
    width: "100%",
    mt: "1rem",
    background: white,
    p: "2rem 2rem 0.25rem",
    ...(!desktopView
      ? {
          background: "none",
          boxShadow: "none",
          p: 0,
        }
      : {}),
  };
  const titleStyle = { mb: "0.5rem", fontSize: mobileView ? "1rem" : "1.5rem" };
  const descStyle = {
    width: desktopView ? "60%" : "100%",
    lineHeight: 1.5,
    opacity: 0.5,
  };
  const cardContainerStyle = {
    display: "flex",
    m: "1rem 0",
    flexWrap: !desktopView && "wrap",
  };
  const yourProgressStyle = {
    mb: !desktopView && "1.75rem",
    "& > div": {
      display: "flex",
      justifyContent: "space-between",
      position: "relative",
      alignItems: "center",
      width: "100%",
      flexWrap: mobileView && "wrap",
    },
  };
  const scoreIconStyle = { height: "1.5rem", width: "auto", fill: green[2] };
  const targetScoreAreaStyle = {
    borderRadius: "0.5rem",
    display: "flex",
    justifyContent: "space-between",
    mt: mobileView && "1rem",
  };
  const targetScoreItemStyle = { width: mobileView ? "45%" : "50%" };
  const ticketIconStyle = { height: "85%", "& .cls-1": { fill: blue[1] } };
  const targetScoreTextStyle = {
    width: mobileView ? "45%" : "50%",
    "& div:last-child p": { color: blue[1] },
  };
  const greyBoxThing = {
    position: "absolute",
    transform: "translate(0, -50%)",
    top: mobileView ? "calc(27.5% - 0.5rem)" : "50%",
    backgroundColor: desktopView ? "#F4F1F8" : white,
    height: "2rem",
    width: "100%",
  };
  const otherGreyBoxThing = {
    position: "absolute",
    transform: "translate(-50%, 0)",
    right: "calc(22.5% - 2rem)",
    backgroundColor: desktopView ? "#F4F1F8" : white,
    width: "2rem",
    height: "100%",
  };
  const bottomAreaContainerStyle = {
    display: "flex",
    width: desktopView ? "47.5%" : "100%",
    flexDirection: mobileView && "column",
  };
  const lastWeekStyle = {
    ml: desktopView && "1.5rem",
    borderLeft: desktopView && "1px solid #E9E8ED",
    pl: desktopView && "1.5rem",
    mr: !mobileView && "1.5rem",
    borderRight: !mobileView && "1px solid #E9E8ED",
    pr: !mobileView && "1.5rem",
    height: mobileView ? "11rem" : "auto",
    mb: mobileView && "1.75rem",
    "& > div": {
      display: "flex",
    },
  };
  const campaignDetailsStyle = {
    display: "flex",
    flexDirection: "column",
    "& > div:first-child": {
      mb: "0.75rem",
    },
  };

  const ContentDisplay = ({
    top,
    middle,
    bottom,
    style,
    centerColour = green[2],
  }) => {
    const isString = (value) => typeof value === "string";

    const contentContainerStyle = {
      aspectRatio: "1/1",
      display: "flex",
      flexFlow: "column",
      alignItems: "center",
      justifyContent: "center",
      p: "0.5rem",
      textAlign: "center",
      ...style,
    };
    const topStyle = {
      fontWeight: 600,
      fontSize: "0.875rem",
    };
    const itemStyle = {
      fontSize: "2rem",
      fontWeight: 600,
      color: centerColour,
    };
    const centerStyle = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    };
    const bottomStyle = {
      fontSize: "0.75rem",
      fontWeight: 500,
      lineHeight: 1.25,
      color: "#100736",
    };

    return (
      <Grid container direction="column" sx={contentContainerStyle}>
        {top && (
          <Grid item xs={4} sx={centerStyle}>
            <Typography sx={topStyle}>{top}</Typography>
          </Grid>
        )}
        <Grid item sx={centerStyle} xs={4}>
          {isString(middle) ? (
            <Typography sx={itemStyle}>{middle}</Typography>
          ) : (
            middle
          )}
        </Grid>
        <Grid item sx={centerStyle} xs={4}>
          {isString(bottom) ? (
            <Typography sx={bottomStyle}>{bottom}</Typography>
          ) : (
            bottom
          )}
        </Grid>
      </Grid>
    );
  };

  const ContentBox = ({ noFill, style, width, children }) => {
    const mainContainerStyle = {
      width: width || "100%",
      height: desktopView ? "100%" : "fit-content",
      border: "3px solid #F4F1F8",
      backgroundColor: noFill ? white : "#F4F1F8",
      borderRadius: "0.5rem",
      zIndex: 1,
      ...(!desktopView
        ? {
            backgroundColor: noFill ? "#F4F1F8" : white,
            borderColor: white,
          }
        : {}),
      ...style,
    };

    return <Box sx={mainContainerStyle}>{children}</Box>;
  };

  const LastWeekCard = ({ title, min, max, tix }) => {
    const containerStyle = {
      border: "3px solid",
      backgroundColor: mobileView ? "#F4F1F8" : white,
      borderColor: mobileView ? white : "#F4F1F8",
      borderRadius: "0.5rem",
      display: "flex",
      flexDirection: "column",
      flex: 1,
      padding: "1rem",
      pb: "0.5rem",
    };
    const titleStyle = {
      textAlign: "center",
      fontWeight: 600,
      fontSize: "0.875rem",
    };
    const contentAreaStyle = { display: "flex", flex: 1 };
    const gridContainerStyle = { alignItems: "center" };
    const textStyle = {
      fontSize: "1.75rem",
      fontWeight: 700,
      color: green[2],
      display: "flex",
      alignItems: "center",
    };
    const arrowStyle = {
      color: green[2],
      fontSize: "0.5rem",
      m: "0 0.25rem",
    };
    const centerStyle = {
      display: "flex",
      alignItems: "center",
    };
    const scoreIconStyle = { height: "1.5rem", width: "auto", fill: green[2] };
    const endStyle = { display: "flex", alignItems: "flex-end" };
    const tixTextStyle = {
      fontSize: "2rem",
      fontWeight: 700,
      color: blue[1],
    };
    const ticketIconStyle = { height: "2rem", "& .cls-1": { fill: blue[1] } };

    return (
      <Box sx={containerStyle}>
        <Typography variant="h2" sx={titleStyle}>
          {title}
        </Typography>
        <Box sx={contentAreaStyle}>
          <Grid
            container
            direction="column"
            spacing={1}
            sx={gridContainerStyle}>
            <Grid item xs={6} sx={endStyle}>
              <Typography sx={textStyle}>
                {min}
                <ArrowForwardIcon sx={arrowStyle} />
                {max}
              </Typography>
            </Grid>
            <Grid item xs={6} sx={centerStyle}>
              <TotalIcon style={scoreIconStyle} />
            </Grid>
          </Grid>
          <Grid
            container
            direction="column"
            spacing={1}
            sx={gridContainerStyle}>
            <Grid item xs={6} sx={endStyle}>
              <Typography sx={tixTextStyle}>{tix}</Typography>
            </Grid>
            <Grid item xs={6} sx={centerStyle}>
              <Box sx={ticketIconStyle}>
                <Ticket height={"100%"} />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  const middleBoxData = (data) => {
    if (!data.currScore) {
      return {
        top: null,
        middle: (
          <Box
            component="img"
            src="images/safeRoads/SelfImprovement-NoTryps-Graphics-09.svg"
            width={"85%"}
          />
        ),
        bottom: t("trypNeeded"),
      };
    }
    if (!data.pointsNeeded) {
      return {
        top: `Current: ${data.currScore}`,
        middle: <CheckCircleIcon sx={{ color: purple[1] }} />,
        bottom: t("gotPoints"),
      };
    }

    return {
      top: `Current: ${data.currScore}`,
      middle: `${data.pointsNeeded}`,
      bottom: t("needPoints"),
      centerColour: purple[1],
    };
  };

  return data ? (
    <ReBox
      style={containerStyle}
      divProps={{ id: CHALLENGES_SELF_IMPROVEMENT_AREA }}>
      <Typography variant="h2" sx={titleStyle}>
        {t("selfImprovementTitle")}
      </Typography>
      <Typography sx={descStyle}>{data.description}</Typography>
      <Box sx={cardContainerStyle}>
        <MainAreaSection
          title={t("yourProgress", { type: data.score_type })}
          width={desktopView ? "52.5%" : "100%"}
          style={yourProgressStyle}>
          <ContentBox width={mobileView ? "45%" : "23.5%"}>
            <ContentDisplay
              top={t("lastWeek")}
              middle={`${data.lwScore}`}
              bottom={<TotalIcon style={scoreIconStyle} />}
            />
          </ContentBox>
          <ContentBox width={mobileView ? "45%" : "23.5%"} noFill>
            <ContentDisplay {...middleBoxData(data)} />
          </ContentBox>
          <ContentBox
            width={mobileView ? "100%" : "47%"}
            style={targetScoreAreaStyle}>
            <ContentDisplay
              top={t("targetScore")}
              middle={`${data.targetScore}`}
              bottom={<TotalIcon style={scoreIconStyle} />}
              style={targetScoreItemStyle}
            />
            <ContentDisplay
              top={t("earnOneEntry")}
              middle={
                <Box sx={ticketIconStyle}>
                  <Ticket height="100%" />
                </Box>
              }
              bottom={t("earnEntryDesc")}
              style={targetScoreTextStyle}
            />
          </ContentBox>
          <Box sx={greyBoxThing} />
          {mobileView && <Box sx={otherGreyBoxThing} />}
        </MainAreaSection>
        <Box sx={bottomAreaContainerStyle}>
          <MainAreaSection
            title={t("lastWeek")}
            width={mobileView ? "100%" : "45%"}
            style={lastWeekStyle}>
            <LastWeekCard
              title={data.lwEntry ? t("goodWork") : t("keepTrying")}
              min={data.lwStart}
              max={data.lwScore}
              tix={data.lwEntry}
            />
          </MainAreaSection>
          <MainAreaSection
            title={t("campaignDetails")}
            style={campaignDetailsStyle}
            width={mobileView ? "100%" : "55%"}>
            <SISideCard icon={<GiftIcon />} text={data.prizeDesc} />
            <SISideCard
              icon={<CalendarIcon />}
              text={t("campaignEnd", { endDate })}
            />
          </MainAreaSection>
        </Box>
      </Box>
      <PreviousWinners winnersData={winners} type="weekImprovement" />
    </ReBox>
  ) : (
    <></>
  );
};

export default SelfImprovement;
